import {
    DialogResult,
    GeException,
    getUrlParameter,
    OnSendNotification,
} from '../infrastructure';
import { endAPMValidationModalSpan } from '../infrastructure/utils';
import { GlobalServiceInterface } from './GeApi';
import { Identity, RecipientForDialog, RecipientsByAllTypes, ValidationDialogResponse } from './types';
// import { OnSendNotification } from './onSendHook';

export namespace GePortals {
    export interface EmailPortals {
        display_name?: string;
        email: string,
        portals?: string[],
        selectedPortal?: string,
        reason?: string,
        type?: string,
        reason_url?: string | null,
        can_override?: boolean
    }
}


/**
 * This class manages the portals associated with external email addresses.
 */
export class GePortals {

    protected globalServices: GlobalServiceInterface;

    public constructor(globalServices: GlobalServiceInterface ) {
        this.globalServices = globalServices;
    }

    /**
     * It saves the external email addresses to a common storage, so that the pop-up windows can access the data
     * @param dialogUuid a unique identifier of the validation dialog
     * @param recipients the list of recipients to be stored to the storage
     */
    public saveExternalAddresses(recipients: RecipientsByAllTypes, dialogUuid: string) {
        localStorage.setItem(`recipients_${dialogUuid}`, JSON.stringify(recipients));
    }

    /**
     * It discards the stored space, so that these copies are not lingering after use
     * @param dialogUuid a unique identifier of the validation dialog
     */
    protected discardExternalAddresses(dialogUuid: string) {
        localStorage.removeItem(`recipients_${dialogUuid}`);
    }

    protected discardBlockedRecipients(dialogUuid: string) {
        localStorage.removeItem(`blocked_recipients_${dialogUuid}`);
    }

    /**
     * Loads the recipients from the common storage
     * @param dialogUuid a unique identifier of the validation dialog
     */
    public loadExternalAddresses(dialogUuid: string): RecipientsByAllTypes {
        const recipients = localStorage.getItem(`recipients_${dialogUuid}`);
        return recipients ? JSON.parse(recipients) : null;
    }

    public saveBlockedRecipients(recipients: RecipientForDialog[], dialogUuid: string) {
        localStorage.setItem(`blocked_recipients_${dialogUuid}`, JSON.stringify(recipients));
    }

    public loadBlockedRecipients(dialogUuid: string): RecipientForDialog[] {
        const recipients = localStorage.getItem(`blocked_recipients_${dialogUuid}`);
        return recipients ? JSON.parse(recipients) : [];
    }

    public loadIdentity(dialogUuid: string): Identity | null {
        const identity = localStorage.getItem(`identity_${dialogUuid}`);
        return identity ? JSON.parse(identity) : null;
    }

    protected discardIdentity(dialogUuid: string) {
        localStorage.removeItem(`identity_${dialogUuid}`);
    }
    /**
     * This function triggers a new popup for the interactive email validation.
     */
    public async showValidationDialog(dialogUuid: string, tenant: string, trace_id: string, transaction_id: string): Promise<ValidationDialogResponse> {
        let dialogHandle: Office.Dialog;

        return new Promise<ValidationDialogResponse>(async (resolve, reject) => {

            try {
                dialogHandle = await this.globalServices.outlookActions
                    .showDialog(`https://${window.location.host}/?portal=true&uuid=${dialogUuid}&deployment=${tenant}&trace_id=${trace_id}&transaction_id=${transaction_id}&server_url=${getUrlParameter("server_url")}`, 56, window.innerWidth > 500 ? 45 : 65 ,
                    (result: DialogResult) => {

                try {
                    // close Dialog through x button OR escape key (not in classic windows though)
                    // https://github.com/OfficeDev/office-js/issues/5200
                    if (!result.message && result.error === 12006) {
                        return reject(new GeException('hook.cancelled', 'Email was cancelled'));
                    }
                    const reply = JSON.parse(result.message) as OnSendNotification;
                    if (!this.globalServices.outlookActions.dialogEventHandler(result)) {
                        const portalItems = this.loadBlockedRecipients(dialogUuid);

                        if (portalItems && reply.success) {
                            resolve({ portalItems, headersResponse: reply?.headersResponse });
                        }
                        if (!reply.success && reply.message === "no_recipients") {
                            reject(new GeException("hook.no_recipients", 'No valid recipients left after attempt to validate, unable to send message.'));
                        }
                        if (!reply.success && reply.message === "User cancelled operation") {
                            reject(new GeException('hook.cancelled', 'Email was cancelled'));
                        } else {
                            reject (new GeException(reply.message, `${JSON.stringify(result)}`));
                        }
                    }
                    else {
                        reject(new GeException("hook.popup_dialog_closed", 'Popup dialog was closed'));
                    }
                } catch(e) {
                    reject(e);
                }

                endAPMValidationModalSpan();
                dialogHandle.close();
                this.discardExternalAddresses(dialogUuid);
                this.discardBlockedRecipients(dialogUuid);
                });
            } catch (openError) {
                // displayDialogAsync itself failed. Without this the throw escapes the async executor
                // as an unhandled rejection and the outer promise never settles at all - the one way
                // this path really can wedge the send.
                endAPMValidationModalSpan();
                this.discardExternalAddresses(dialogUuid);
                this.discardBlockedRecipients(dialogUuid);

                reject(openError);
            }
        });
    }

    /**
     * This function triggers a new popup for the interactive email validation.
     */
    public async showUserOnBoardingDialog(dialogUuid: string, tenant: string, trace_id: string, transaction_id: string ): Promise<Identity> {
        return new Promise<Identity>(async (resolve, reject) => {
            const platformInfoRaw = localStorage.getItem("platformInfo");
            const platform = platformInfoRaw ? JSON.parse(platformInfoRaw)?.platform : undefined;
            // a bit more height for clients.
            const dialogHeight = platform !== 'web' ? 40 : 35;
            const dialogHandle = await this.globalServices.outlookActions
                .showDialog(`https://${window.location.host}/onboarding?portal=true&uuid=${dialogUuid}&deployment=${tenant}&trace_id=${trace_id}&transaction_id=${transaction_id}&server_url=${getUrlParameter("server_url")}`, dialogHeight, 40 ,
                (result: DialogResult) => {

                try {
                    // close Dialog through x button OR escape key (not in classic windows though)
                    // https://github.com/OfficeDev/office-js/issues/5200
                    if (!result.message && result.error === 12006) {
                        return reject(new GeException('hook.cancelled', 'Email was cancelled'));
                    }
                    const reply = JSON.parse(result.message) as OnSendNotification;
                    if (!this.globalServices.outlookActions.dialogEventHandler(result)) {
                        const identity = this.loadIdentity(dialogUuid);
                        if (reply.success && identity) {
                            resolve(identity);
                        }
                        if (!reply.success && reply.message === "User cancelled operation") {
                            reject(new GeException('hook.cancelled', 'Email was cancelled'));
                        } else {
                            reject (new GeException(reply.message, `${JSON.stringify(result)}`));
                        }
                    }
                    else {
                        reject(new GeException("hook.popup_dialog_closed", 'Popup dialog was closed'));
                    }
                } catch(e) {
                    reject(e);
                }

                endAPMValidationModalSpan();
                dialogHandle.close();
                this.discardIdentity(dialogUuid);
            });
        });   
    }
}